import { Trans, useLingui } from '@lingui/react/macro'
import { Avatar, Badge, Card, Group, Stack, Text, Rating } from '@mantine/core'
import type { VisitWithRelations } from '@markku/types'
import { getCategoryColor, getCategoryLabel } from '@markku/utils'
import { useNavigate } from '@tanstack/react-router'

interface VisitListItemProps {
  visit: VisitWithRelations
}

export function VisitListItem({ visit }: VisitListItemProps) {
  const { t } = useLingui()
  const navigate = useNavigate()

  const visitDate = new Date(visit.startTime)
  const endTime = new Date(visit.endTime)
  const day = visitDate.getDate()
  const month = visitDate.toLocaleDateString('fi-FI', { month: 'short' })
  const startTimeStr = visitDate.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const endTimeStr = endTime.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })

  // Check if visit is in the past (completed or past date)
  const isPastVisit =
    visit.status === 'completed' || new Date(visit.startTime) < new Date()
  const feedback = visit.feedbacks?.[0]

  // Format date and time as title
  const weekday = visitDate.toLocaleDateString('fi-FI', { weekday: 'short' })
  const dateTimeTitle = t`${weekday} ${day}. ${month}, at ${startTimeStr}-${endTimeStr}`

  // Extract unique task categories
  const taskCategories = visit.visitTasks
    ? [
        ...new Set(
          visit.visitTasks
            .map((task) => task.maintenanceTask?.category)
            .filter(Boolean),
        ),
      ]
    : []

  // Get attendee initials for avatar
  const attendee = visit.visitAttendees?.[0]?.user
  const attendeeInitials = attendee
    ? `${attendee.firstName[0]}${attendee.lastName[0]}`
    : 'M' // Default to M for Markku

  return (
    <Card
      withBorder
      radius="lg"
      shadow="xs"
      p="md"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate({ to: `/visits/${visit.id}` })}
    >
      <Stack gap="sm">
        {/* Header row with date/time title and avatar */}
        <Group justify="space-between" align="center">
          <Text fw={600} size="lg" style={{ flex: 1 }}>
            {dateTimeTitle}
          </Text>
          <Avatar size="sm" radius="xl" color="brandOrange">
            {attendeeInitials}
          </Avatar>
        </Group>

        {/* Task category badges */}
        {taskCategories.length > 0 ? (
          <Group gap="xs">
            {taskCategories.map((category) => (
              <Badge
                key={category}
                size="md"
                variant="light"
                color={getCategoryColor(category as string)}
              >
                {getCategoryLabel(category as string)}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            <Trans>No tasks</Trans>
          </Text>
        )}

        {/* Notes if available */}
        {visit.notes && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {visit.notes}
          </Text>
        )}

        {/* Feedback section for past visits */}
        {isPastVisit &&
          (feedback ? (
            <Group gap={6}>
              <Rating value={feedback.ratingValue} readOnly size="sm" />
            </Group>
          ) : (
            <Badge size="md" variant="light" color="orange">
              <Trans>Give feedback</Trans>
            </Badge>
          ))}
      </Stack>
    </Card>
  )
}
